import {
  Auth,
  AuthProvider,
  signInWithPopup,
  UserCredential
} from 'firebase/auth';

const FIREBASE_ERROR_FALLBACK_MESSAGE = 'somethingWentWrong';

/** Translation keys for auth errors (Firebase codes + SSO messages). */
export const AUTH_ERROR_MESSAGE_KEYS = {
  SSO_ACCOUNT_LINKED: 'ssoAccountLinked',
  SSO_ACCOUNT_LINK_FAILED: 'ssoAccountLinkFailed',
  SSO_USER_NOT_ASSIGNED: 'ssoUserNotAssignedToApp',
  SSO_USER_REMOVED_FROM_TENANT: 'ssoUserRemovedFromTenant',
  NO_APP_ACCOUNT_FOR_SSO_USER: 'noAppAccountForSsoUser',
  USER_ACCOUNT_DISABLED: 'userAccountDisabled',
  USER_NOT_FOUND: 'userNotFound',
  WRONG_PASSWORD: 'wrongPassword'
} as const;

/** Why mid-session Entra revalidation forced logout. */
export type EntraAccessRevokeReason = 'not_assigned' | 'user_removed';

const FIREBASE_ERROR_MAP: Record<string, string> = {
  'auth/user-not-found': AUTH_ERROR_MESSAGE_KEYS.USER_NOT_FOUND,
  'auth/wrong-password': AUTH_ERROR_MESSAGE_KEYS.WRONG_PASSWORD,
  'auth/user-disabled': AUTH_ERROR_MESSAGE_KEYS.USER_ACCOUNT_DISABLED
};

export const handleFirebaseAuthError = (code: string): string => {
  return FIREBASE_ERROR_MAP[code] ?? FIREBASE_ERROR_FALLBACK_MESSAGE;
};

const getAuthErrorText = (error: unknown): string => {
  const authError = error as {
    code?: string;
    message?: string;
    errorCode?: string;
    errorMessage?: string;
    customData?: Record<string, unknown>;
  };

  return [
    authError.message,
    authError.errorMessage,
    authError.code,
    authError.errorCode,
    JSON.stringify(authError.customData ?? {})
  ]
    .filter(Boolean)
    .join(' ');
};

export const isSsoPopupCancelledError = (error: unknown): boolean => {
  const code = (error as { code?: string })?.code;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
};

/**
 * `signInWithPopup` that also rejects when the opener regains focus, Firebase never
 * settles, and no user was signed in (common when closing the Microsoft account picker).
 * If a user is already present after focus, we keep waiting for Firebase's own settle
 * so a slow successful login is not treated as cancel.
 */
export const signInWithAuthPopup = (
  auth: Auth,
  provider: AuthProvider
): Promise<UserCredential> =>
  new Promise((resolve, reject) => {
    let settled = false;
    let focusTimeoutId: number | undefined;

    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      if (focusTimeoutId !== undefined) {
        window.clearTimeout(focusTimeoutId);
      }
      window.removeEventListener('focus', onFocus);
      callback();
    };

    const onFocus = () => {
      if (settled) {
        return;
      }
      if (focusTimeoutId !== undefined) {
        window.clearTimeout(focusTimeoutId);
      }
      // Give Firebase a moment to settle after the popup closes.
      focusTimeoutId = window.setTimeout(() => {
        if (settled) {
          return;
        }
        // Successful OAuth often sets currentUser before the promise resolves.
        if (auth.currentUser) {
          return;
        }
        finish(() => {
          reject(
            Object.assign(new Error('Sign-in popup was closed.'), {
              code: 'auth/popup-closed-by-user'
            })
          );
        });
      }, 1000);
    };

    window.addEventListener('focus', onFocus);

    signInWithPopup(auth, provider).then(
      (result) => finish(() => resolve(result)),
      (error) => finish(() => reject(error))
    );
  });

export const isAccountExistsWithDifferentCredentialError = (error: unknown): boolean =>
  (error as { code?: string })?.code === 'auth/account-exists-with-different-credential';

/** Entra AADSTS50105 when the user is not assigned to the enterprise app. */
export const isEntraUserNotAssignedError = (error: unknown): boolean => {
  const text = getAuthErrorText(error);
  return text.includes('AADSTS50105') || /not assigned to a role for the application/i.test(text);
};

/**
 * Entra errors when the user was deleted/disabled or no longer exists in the tenant
 * (Entra ID → Users), distinct from enterprise-app assignment (AADSTS50105).
 */
export const isEntraUserRemovedFromTenantError = (error: unknown): boolean => {
  const text = getAuthErrorText(error);
  return (
    text.includes('AADSTS50034') ||
    text.includes('AADSTS50057') ||
    text.includes('AADSTS50020') ||
    text.includes('AADSTS51004') ||
    /user account is disabled/i.test(text) ||
    /user account .+ does not exist in (the )?.*directory/i.test(text) ||
    /does not exist in tenant/i.test(text)
  );
};

/** Map a known Entra access-revoke error to a reason, or null if not revoke-worthy. */
export const getEntraAccessRevokeReason = (error: unknown): EntraAccessRevokeReason | null => {
  if (isEntraUserNotAssignedError(error)) {
    return 'not_assigned';
  }
  if (isEntraUserRemovedFromTenantError(error)) {
    return 'user_removed';
  }
  return null;
};

export const getEntraAccessRevokeMessageKey = (reason: EntraAccessRevokeReason): string => {
  if (reason === 'user_removed') {
    return AUTH_ERROR_MESSAGE_KEYS.SSO_USER_REMOVED_FROM_TENANT;
  }
  return AUTH_ERROR_MESSAGE_KEYS.SSO_USER_NOT_ASSIGNED;
};

export const handleSsoSignInError = (error: unknown): string => {
  const revokeReason = getEntraAccessRevokeReason(error);
  if (revokeReason) {
    return getEntraAccessRevokeMessageKey(revokeReason);
  }

  const code = (error as { code?: string })?.code ?? '';
  return handleFirebaseAuthError(code);
};

export const isMissingAppUserError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 404;
};
